import webStorage from '@/common/webStorage'

/**
 * Registro de acesso (AuthenticationLog).
 *
 * A API grava uma linha por empresa, usuário e dia. O refresh é registrado pela própria API; o
 * front só avisa quando a sessão entra numa empresa, porque o login não carrega empresa:
 * - `Login`: primeira entrada depois de um login (senha, 2FA ou SSO).
 * - `TenantSwitch`: as demais entradas, pela tela de troca de empresa.
 */
export type TenantAccessSource = 'Login' | 'TenantSwitch'

export interface TenantAccess {
  source: TenantAccessSource
  sso: boolean
}

// sessionStorage: a pendência vale só para a aba em que o login aconteceu.
const PENDING_LOGIN_KEY = 'pending-login'

/** Marca que houve um login nesta aba e a próxima entrada numa empresa deve contar como Login. */
export const markPendingLogin = (sso: boolean) => {
  webStorage.setItem(PENDING_LOGIN_KEY, { sso }, false)
}

export const hasPendingLogin = (): boolean => !!webStorage.getItem(PENDING_LOGIN_KEY)

export const clearPendingLogin = () => {
  webStorage.removeItem(PENDING_LOGIN_KEY)
}

/**
 * Consome a pendência de login: a primeira entrada depois do login conta como Login e as demais
 * como troca de empresa.
 */
export const takeTenantAccess = (): TenantAccess => {
  const pending = webStorage.getItem(PENDING_LOGIN_KEY)
  clearPendingLogin()

  return pending
    ? { source: 'Login', sso: !!pending.sso }
    : { source: 'TenantSwitch', sso: false }
}

/** Authorization com que a requisição que falhou foi enviada. */
const sentAuthorization = (error: any): string | undefined => {
  const headers = error?.config?.headers ?? error?.response?.config?.headers
  const value = typeof headers?.get === 'function' ? headers.get('Authorization') : headers?.Authorization

  return value ? String(value) : undefined
}

/**
 * Indica que a requisição foi enviada com um token que já foi renovado: o 401 chegou depois do
 * refresh terminar. Nesse caso basta reenviar a requisição, sem renovar de novo.
 */
export const wasSentWithOldToken = (error: any, currentAuthorization: string | undefined): boolean => {
  const sent = sentAuthorization(error)

  return !!sent && !!currentAuthorization && sent !== currentAuthorization
}
